import {
  Button,
  DrawerBody,
  DrawerFooter,
  Flex,
  Spinner,
} from '@chakra-ui/react';
import { offerApi } from '@texas/api/endpoints/offerApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { Icons } from '@texas/components/shared/Icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { offerEvents } from '../events';
import { BranchInquirySection } from './BranchInquirySection';
import {
  getDefaultSelectedIds,
  groupInquiriesByBranch,
} from './createOfferPreview';
import { CreateOfferReviewStatus } from './CreateOfferReviewStatus';

interface CreateOfferDrawerContentProps {
  variantId: number;
  onClose: () => void;
}

export function CreateOfferDrawerContent({
  variantId,
  onClose,
}: CreateOfferDrawerContentProps) {
  const { t } = useTranslation();
  const {
    data: preview,
    error: previewError,
    loading,
    refetch,
  } = useApiResource(offerApi.getPreview);
  const {
    request: createOffer,
    error: createError,
    loading: creating,
  } = useApiRequest(offerApi.createOffer);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    setSelectedIds([]);
    refetch(variantId);
  }, [variantId, refetch]);

  useEffect(() => {
    if (preview) setSelectedIds(getDefaultSelectedIds(preview));
  }, [preview]);

  const selectedInquiries = useMemo(
    () =>
      preview?.acceptedInquiries.filter((inquiry) =>
        selectedIds.includes(inquiry.id),
      ) ?? [],
    [preview, selectedIds],
  );
  const inquiriesByBranch = useMemo(
    () => groupInquiriesByBranch(preview?.acceptedInquiries ?? []),
    [preview],
  );
  const selectedInquiriesByBranch = useMemo(
    () => groupInquiriesByBranch(selectedInquiries),
    [selectedInquiries],
  );
  const hasSelectedTiers = selectedInquiries.some(
    (inquiry) => inquiry.tiers.length > 0,
  );
  const selectedBranchCount = selectedInquiriesByBranch.size;
  const awaitingBranchCount = Math.max(
    0,
    (preview?.branches.length ?? 0) - selectedBranchCount,
  );
  const toggleInquiry = (id: number) => {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id],
    );
  };

  const handleCreate = async () => {
    if (selectedIds.length === 0) return;

    const response = await createOffer({
      variantId,
      inquiryIds: selectedIds,
    });
    if (!response.hasError) {
      offerEvents.onUpdated.dispatch();
      onClose();
    }
  };

  return (
    <>
      <DrawerBody px={6} py={5}>
        {loading && (
          <Flex justify="center" py={16}>
            <Spinner />
          </Flex>
        )}
        {previewError && <ErrorDetails error={previewError} />}
        {!loading && preview && (
          <Flex direction="column" gap={7}>
            <BranchInquirySection
              awaitingCount={awaitingBranchCount}
              branches={preview.branches}
              inquiriesByBranch={inquiriesByBranch}
              selectedCount={selectedBranchCount}
              selectedIds={selectedIds}
              onToggle={toggleInquiry}
            />
            {createError && <ErrorDetails error={createError} />}
          </Flex>
        )}
      </DrawerBody>
      <DrawerFooter
        alignItems={{ base: 'stretch', sm: 'center' }}
        bg="offerDrawer.raisedSurface"
        borderTop="1px solid"
        borderColor="offerDrawer.border"
        flexDirection={{ base: 'column', sm: 'row' }}
        gap={3}
        justifyContent="space-between"
        px={6}
        py={4}
      >
        <CreateOfferReviewStatus
          hasSelection={selectedInquiries.length > 0}
          hasTiers={hasSelectedTiers}
        />
        <Flex gap={2} justify="flex-end">
          <Button variant="ghost" onClick={onClose}>
            {t('general.cancel')}
          </Button>
          <Button
            isDisabled={
              !preview ||
              preview.branches.length === 0 ||
              selectedInquiries.length === 0 ||
              !hasSelectedTiers
            }
            isLoading={creating}
            rightIcon={<Icons.ArrowRight boxSize={4} />}
            variant="texas-solid"
            onClick={handleCreate}
          >
            {t('offer.createDraftOffer')}
          </Button>
        </Flex>
      </DrawerFooter>
    </>
  );
}
